import { productTags } from "./menu.js";
import { PROMOS_PRO_ONLY } from "./plans.js";
import { promosAvailable, promosOf } from "./promos.js";

// Idiomas del menú público. Los textos fijos de la página (botones, carrito, avisos)
// están acá; los del negocio (productos, categorías, descripción, horarios) los traduce
// la IA en n8n (n8n/miqrapp-traducir-menu.js) y quedan guardados en traducciones/{usuarioEmpresa}.

export const LANGS = [
    { code: "es", short: "ES", name: "Español" },
    { code: "en", short: "EN", name: "English" },
    { code: "pt", short: "PT", name: "Português" }
];
export const DEFAULT_LANG = "es";
export const LANG_PARAM = "lang";
const STORAGE_KEY = "miqrapp-idioma";

const isLang = (code) => LANGS.some((l) => l.code === code);

// Idioma con el que abre el menú: el de ?lang=, el que eligió antes en este navegador,
// o el del teléfono si está en inglés o portugués. Si no, español.
export const detectLang = () => {
    try {
        const param = new URLSearchParams(window.location.search).get(LANG_PARAM);
        if (isLang(param)) return param;
        const saved = localStorage.getItem(STORAGE_KEY);
        if (isLang(saved)) return saved;
    } catch (error) {
        // Sin almacenamiento sigue con el idioma del teléfono.
    }
    const langs = (typeof navigator !== "undefined" && (navigator.languages || [navigator.language])) || [];
    for (const l of langs) {
        const code = String(l || "").slice(0, 2).toLowerCase();
        if (code === "es") return "es";
        if (isLang(code)) return code;
    }
    return DEFAULT_LANG;
}

export const saveLang = (code) => {
    try {
        localStorage.setItem(STORAGE_KEY, code);
    } catch (error) {
        // Si no se puede guardar, la próxima vez vuelve a detectarlo.
    }
}

// Clave de cada texto del negocio en traducciones/: un hash corto del texto limpio.
// Si el dueño cambia un texto, cambia la clave y se vuelve a traducir solo ese.
// Tiene que dar lo mismo que textKey en n8n/miqrapp-traducir-menu.js.
export const cleanText = (text) => String(text ?? "").replace(/\s+/g, " ").trim();

export const textKey = (text) => {
    const s = cleanText(text);
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return "t" + h.toString(36) + s.length.toString(36);
}

// Los textos del negocio que se traducen. El nombre del negocio no se traduce.
// Tiene que coincidir con lo que junta "Juntar Textos" en n8n/miqrapp-traducir-menu.js.
export const menuTexts = (business, products) => {
    const texts = [business.descripcionEmpresa, business.horario, business.horarioSec];
    for (const p of products) {
        texts.push(p.name, p.description, p.category);
        texts.push(...productTags(business, p).filter((t) => !fixedTag("en", t)));
    }
    // Promociones del banner (Pro), las activas aunque hoy no estén en horario.
    if (promosAvailable(business, PROMOS_PRO_ONLY)) {
        promosOf(business).filter((p) => p.activa).forEach((p) => texts.push(p.titulo, p.texto));
    }
    return [...new Set(texts.map(cleanText).filter(Boolean))];
}

// Etiquetas sugeridas del panel (PRODUCT_TAGS): van traducidas acá, sin IA.
const TAGS = {
    en: { "Nuevo": "New", "Recomendado": "Recommended", "Promo": "Deal", "Vegano": "Vegan", "Sin TACC": "Gluten-free", "Picante": "Spicy" },
    pt: { "Nuevo": "Novo", "Recomendado": "Recomendado", "Promo": "Promoção", "Vegano": "Vegano", "Sin TACC": "Sem glúten", "Picante": "Picante" }
};
export const fixedTag = (lang, tag) => TAGS[lang]?.[tag];

const TEXTS = {
    es: {
        all: "Todo",
        search: "Buscar en el menú",
        searchPlaceholder: "Buscar en el menú…",
        searchTag: (tag) => `Ver todo lo que es "${tag}"`,
        categories: "Categorías",
        noResults: (q) => `No encontramos nada con "${q}".`,
        emptyMenu: "Este negocio todavía está armando su menú.",
        madeWith: ["Menú hecho con ", " · Creá el tuyo gratis"],
        loadError: "No pudimos cargar el menú. Revisá tu conexión y probá de nuevo.",
        retry: "Reintentar",
        loading: "Cargando el menú…",
        directions: "Cómo llegar",
        hours: "Horarios",
        seeHours: "Ver horarios",
        rateGoogle: "Calificanos en Google",
        reviewsTitle: "Opiniones en Google",
        leaveReview: "Dejanos tu reseña",
        soldOut: "Agotado",
        soldOutLong: "Agotado por ahora",
        addToOrder: "Agregar al pedido",
        yourOrder: "Tu pedido",
        viewOrder: "Ver mi pedido",
        prevSlide: "Anterior",
        nextSlide: "Siguiente",
        slideOf: (i, n) => `${i} de ${n}`,
        photos: (name) => `Fotos de ${name}`,
        promos: "Promociones",
        seeProduct: "Ver producto",
        backToTop: "Volver arriba",
        language: "Idioma del menú",
        translating: "Traduciendo el menú…",
        translateFailed: "No pudimos traducir todo el menú. Lo que falta se ve en español.",
        qtyAdd: (n) => `Agregar ${n} al pedido`,
        qtyGroup: (n) => `${n} en el pedido`,
        qtyRemove: (n) => `Sacar ${n} del pedido`,
        qtyLess: (n) => `Uno menos de ${n}`,
        qtyMore: (n) => `Uno más de ${n}`,
        each: "c/u",
        doneTitle: "¡Listo! Terminá de enviar el mensaje en WhatsApp.",
        doneText: (b) => `${b} te va a confirmar el pedido por ahí.`,
        openWhatsappAgain: "Abrir WhatsApp de nuevo",
        backToMenu: "Volver al menú",
        emptyCart: "Tu pedido está vacío. Tocá el + de un producto para sumarlo.",
        yourData: "Tus datos",
        yourName: "Tu nombre (opcional)",
        delivery: "Entrega (opcional)",
        pickup: "Retiro en el local",
        shipping: "Envío a domicilio",
        address: "Dirección de entrega",
        notes: "Aclaraciones (opcional)",
        notesPlaceholder: "Ej.: pago en efectivo",
        total: "Total",
        products: (n) => `${n} ${n === 1 ? "producto" : "productos"}`,
        toConfirm: "A confirmar",
        free: "gratis",
        freeCap: "Gratis",
        noShipping: "El total no incluye el envío a domicilio.",
        noShippingConfirm: (b) => `El total no incluye el envío: ${b} te confirma el costo por WhatsApp.`,
        payMp: "Pagar con Mercado Pago",
        paying: "Preparando el pago…",
        sendOrderPayLater: "Enviar por WhatsApp y pagar al recibir",
        payNote: (b) => `Pagás online con Mercado Pago y después le avisás a ${b} por WhatsApp.`,
        payFailed: "No pudimos iniciar el pago con Mercado Pago. Probá de nuevo o mandá el pedido por WhatsApp.",
        payChecking: "Revisando tu pago…",
        payCheckFailed: "No pudimos revisar el pago.",
        payApproved: "¡Pago aprobado!",
        payApprovedText: (b) => `Avisale a ${b} por WhatsApp así prepara tu pedido.`,
        payApprovedNoMsg: (b) => `${b} ya ve tu pedido como pagado.`,
        payPending: "Tu pago está en proceso",
        payPendingText: (b) => `Mercado Pago lo está revisando. Avisale a ${b} por WhatsApp: el pedido figura con el pago en proceso.`,
        payNotDone: "El pago no se completó",
        payNotDoneText: "Podés intentar de nuevo o mandar el pedido por WhatsApp y pagar al recibir.",
        payRetry: "Intentar el pago de nuevo",
        notifyWhatsapp: "Avisar por WhatsApp",
        orderLabel: (n) => `Pedido ${n}`,
        sendOrder: "Enviar pedido por WhatsApp",
        sendNote: (b) => `Se abre WhatsApp con el pedido escrito para que lo mandes a ${b}.`
    },
    en: {
        all: "All",
        search: "Search the menu",
        searchPlaceholder: "Search the menu…",
        searchTag: (tag) => `See everything tagged "${tag}"`,
        categories: "Categories",
        noResults: (q) => `Nothing found for "${q}".`,
        emptyMenu: "This business is still putting its menu together.",
        madeWith: ["Menu made with ", " · Create yours for free"],
        loadError: "We couldn't load the menu. Check your connection and try again.",
        retry: "Try again",
        loading: "Loading the menu…",
        directions: "Directions",
        hours: "Hours",
        seeHours: "See hours",
        rateGoogle: "Rate us on Google",
        reviewsTitle: "Google reviews",
        leaveReview: "Leave us a review",
        soldOut: "Sold out",
        soldOutLong: "Sold out for now",
        addToOrder: "Add to order",
        yourOrder: "Your order",
        viewOrder: "View my order",
        prevSlide: "Previous",
        nextSlide: "Next",
        slideOf: (i, n) => `${i} of ${n}`,
        photos: (name) => `Photos of ${name}`,
        promos: "Deals",
        seeProduct: "See item",
        backToTop: "Back to top",
        language: "Menu language",
        translating: "Translating the menu…",
        translateFailed: "We couldn't translate the whole menu. The rest is shown in Spanish.",
        qtyAdd: (n) => `Add ${n} to the order`,
        qtyGroup: (n) => `${n} in the order`,
        qtyRemove: (n) => `Remove ${n} from the order`,
        qtyLess: (n) => `One less ${n}`,
        qtyMore: (n) => `One more ${n}`,
        each: "each",
        doneTitle: "Done! Finish sending the message in WhatsApp.",
        doneText: (b) => `${b} will confirm your order there.`,
        openWhatsappAgain: "Open WhatsApp again",
        backToMenu: "Back to the menu",
        emptyCart: "Your order is empty. Tap the + on a product to add it.",
        yourData: "Your details",
        yourName: "Your name (optional)",
        delivery: "Pickup or delivery (optional)",
        pickup: "Pickup",
        shipping: "Delivery",
        address: "Delivery address",
        notes: "Notes (optional)",
        notesPlaceholder: "E.g.: paying in cash",
        total: "Total",
        products: (n) => `${n} ${n === 1 ? "item" : "items"}`,
        toConfirm: "To be confirmed",
        free: "free",
        freeCap: "Free",
        noShipping: "The total doesn't include delivery.",
        noShippingConfirm: (b) => `The total doesn't include delivery: ${b} will confirm the cost on WhatsApp.`,
        payMp: "Pay with Mercado Pago",
        paying: "Getting the payment ready…",
        sendOrderPayLater: "Send via WhatsApp and pay on delivery",
        payNote: (b) => `Pay online with Mercado Pago, then let ${b} know via WhatsApp.`,
        payFailed: "We couldn't start the Mercado Pago payment. Try again or send the order via WhatsApp.",
        payChecking: "Checking your payment…",
        payCheckFailed: "We couldn't check the payment.",
        payApproved: "Payment approved!",
        payApprovedText: (b) => `Let ${b} know via WhatsApp so they can prepare your order.`,
        payApprovedNoMsg: (b) => `${b} already sees your order as paid.`,
        payPending: "Your payment is being processed",
        payPendingText: (b) => `Mercado Pago is reviewing it. Let ${b} know via WhatsApp: the order shows the payment in progress.`,
        payNotDone: "The payment didn't go through",
        payNotDoneText: "You can try again or send the order via WhatsApp and pay on delivery.",
        payRetry: "Try the payment again",
        notifyWhatsapp: "Notify via WhatsApp",
        orderLabel: (n) => `Order ${n}`,
        sendOrder: "Send order via WhatsApp",
        sendNote: (b) => `WhatsApp opens with your order written out so you can send it to ${b}. The message goes in Spanish so the business understands it.`
    },
    pt: {
        all: "Tudo",
        search: "Buscar no cardápio",
        searchPlaceholder: "Buscar no cardápio…",
        searchTag: (tag) => `Ver tudo com "${tag}"`,
        categories: "Categorias",
        noResults: (q) => `Não encontramos nada com "${q}".`,
        emptyMenu: "Este estabelecimento ainda está montando o cardápio.",
        madeWith: ["Cardápio feito com ", " · Crie o seu grátis"],
        loadError: "Não conseguimos carregar o cardápio. Verifique sua conexão e tente de novo.",
        retry: "Tentar de novo",
        loading: "Carregando o cardápio…",
        directions: "Como chegar",
        hours: "Horários",
        seeHours: "Ver horários",
        rateGoogle: "Avalie-nos no Google",
        reviewsTitle: "Avaliações no Google",
        leaveReview: "Deixe sua avaliação",
        soldOut: "Esgotado",
        soldOutLong: "Esgotado no momento",
        addToOrder: "Adicionar ao pedido",
        yourOrder: "Seu pedido",
        viewOrder: "Ver meu pedido",
        prevSlide: "Anterior",
        nextSlide: "Próxima",
        slideOf: (i, n) => `${i} de ${n}`,
        photos: (name) => `Fotos de ${name}`,
        promos: "Promoções",
        seeProduct: "Ver produto",
        backToTop: "Voltar ao topo",
        language: "Idioma do cardápio",
        translating: "Traduzindo o cardápio…",
        translateFailed: "Não conseguimos traduzir todo o cardápio. O resto aparece em espanhol.",
        qtyAdd: (n) => `Adicionar ${n} ao pedido`,
        qtyGroup: (n) => `${n} no pedido`,
        qtyRemove: (n) => `Tirar ${n} do pedido`,
        qtyLess: (n) => `Um ${n} a menos`,
        qtyMore: (n) => `Mais um ${n}`,
        each: "cada",
        doneTitle: "Pronto! Termine de enviar a mensagem no WhatsApp.",
        doneText: (b) => `${b} vai confirmar seu pedido por lá.`,
        openWhatsappAgain: "Abrir o WhatsApp de novo",
        backToMenu: "Voltar ao cardápio",
        emptyCart: "Seu pedido está vazio. Toque no + de um produto para adicioná-lo.",
        yourData: "Seus dados",
        yourName: "Seu nome (opcional)",
        delivery: "Entrega (opcional)",
        pickup: "Retirar no local",
        shipping: "Entrega em domicílio",
        address: "Endereço de entrega",
        notes: "Observações (opcional)",
        notesPlaceholder: "Ex.: pagamento em dinheiro",
        total: "Total",
        products: (n) => `${n} ${n === 1 ? "produto" : "produtos"}`,
        toConfirm: "A confirmar",
        free: "grátis",
        freeCap: "Grátis",
        noShipping: "O total não inclui a entrega.",
        noShippingConfirm: (b) => `O total não inclui a entrega: ${b} confirma o valor pelo WhatsApp.`,
        payMp: "Pagar com Mercado Pago",
        paying: "Preparando o pagamento…",
        sendOrderPayLater: "Enviar pelo WhatsApp e pagar na entrega",
        payNote: (b) => `Você paga online com Mercado Pago e depois avisa ${b} pelo WhatsApp.`,
        payFailed: "Não conseguimos iniciar o pagamento com Mercado Pago. Tente de novo ou envie o pedido pelo WhatsApp.",
        payChecking: "Verificando seu pagamento…",
        payCheckFailed: "Não conseguimos verificar o pagamento.",
        payApproved: "Pagamento aprovado!",
        payApprovedText: (b) => `Avise ${b} pelo WhatsApp para prepararem seu pedido.`,
        payApprovedNoMsg: (b) => `${b} já vê seu pedido como pago.`,
        payPending: "Seu pagamento está em processamento",
        payPendingText: (b) => `O Mercado Pago está revisando. Avise ${b} pelo WhatsApp: o pedido aparece com o pagamento em processamento.`,
        payNotDone: "O pagamento não foi concluído",
        payNotDoneText: "Você pode tentar de novo ou enviar o pedido pelo WhatsApp e pagar na entrega.",
        payRetry: "Tentar o pagamento de novo",
        notifyWhatsapp: "Avisar pelo WhatsApp",
        orderLabel: (n) => `Pedido ${n}`,
        sendOrder: "Enviar pedido pelo WhatsApp",
        sendNote: (b) => `O WhatsApp abre com o pedido escrito para você enviar a ${b}. A mensagem vai em espanhol para o estabelecimento entender.`
    }
};

// Textos fijos de la página en el idioma elegido.
export const uiTexts = (lang) => TEXTS[lang] || TEXTS[DEFAULT_LANG];
